// src/lib/contractFundReceiver.js — seeding the person who took the company's money.
// PURE, tested by contractFundReceiver.test.mjs. The server's half is lib/fund_receiver.js.
//
// Owner, 2026-09-13: the receiving agent needs `can_receive_company_funds`, and the meeting's
// agent is preselected ONLY if they hold it.
//
// 🔑 THE DECISION IS THE SERVER'S. `draft.fund_receiver.default` is already null when the meeting's
// agent lacks the grant, so nothing here re-derives who is eligible — this only decides whether to
// write the server's answer into an empty field. Re-deriving it in the client is how a permission
// rule ends up with two slightly different copies, one of which is the one that gets edited.

/**
 * @param {object} values the seeded values
 * @param {object} draft  the server's draft payload
 * @returns {object} values, with any EMPTY field of kind `agent` filled from the server's default
 */
export function withFundReceiver(values, draft) {
  const d = draft?.fund_receiver?.default
  if (!d?.party_id) return values
  const out = { ...values }
  for (const f of draft?.fields || []) {
    if (f.kind !== 'agent') continue
    // ⚠️ An inherited or already-answered receiver is somebody's record of who took the money,
    //    never a slot waiting for a suggestion.
    if (out[f.key]?.party_id) continue
    out[f.key] = { party_id: d.party_id, name: d.name || '' }
  }
  return out
}

/** Which agent fields a fund-receiver refusal named, so the form can point at them. Empty for
 *  every other error — the caller renders nothing rather than guessing. */
export function fundReceiverFields(e) {
  const code = e?.message
  if (code !== 'not_authorised_to_receive_funds' && code !== 'fund_receiver_not_authorised') return []
  const f = e?.body?.fields
  return Array.isArray(f) ? f.filter(x => typeof x === 'string') : []
}
